import { useEffect, useState } from 'react';
import { api } from './api';
import { Notice } from './components';
import type { Platform, WindsorAccount } from './types';

const LABELS: Record<Platform, { noun: string; plural: string; where: string }> = {
  linkedin: { noun: 'LinkedIn Page', plural: 'LinkedIn Pages', where: 'LinkedIn Organic' },
  instagram: {
    noun: 'Instagram account',
    plural: 'Instagram accounts',
    where: 'Instagram Insights',
  },
};

/**
 * Pick which Windsor-connected account belongs to this client.
 *
 * One Windsor API key reads every client's accounts, so this choice is what
 * keeps each client's data in their own schema. Accounts already linked to a
 * different client are shown but disabled -- the server refuses them anyway.
 */
export function WindsorPicker({
  slug,
  platform,
  onLinked,
  onCancel,
}: {
  slug: string;
  platform: Platform;
  onLinked: (name: string) => void;
  onCancel: () => void;
}) {
  const label = LABELS[platform];
  const [accounts, setAccounts] = useState<WindsorAccount[] | null>(null);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .windsorAccounts(platform)
      .then(setAccounts)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [platform]);

  async function link() {
    setBusy(true);
    setError('');
    try {
      const connection = await api.linkWindsor(slug, selected, platform);
      onLinked(connection.external_name ?? selected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link');
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginTop: 16, background: 'var(--bg)' }}>
      <h2>Link {label.noun === 'Instagram account' ? 'an' : 'a'} {label.noun} from Windsor</h2>
      <p className="muted" style={{ marginTop: -6 }}>
        {label.plural} appear here once they&rsquo;re selected in your Windsor.ai workspace
        ({label.where}). Choose the one that belongs to this client.
      </p>

      {error && <Notice kind="err">{error}</Notice>}

      {accounts === null && !error && <div className="spinner">Loading Windsor accounts…</div>}

      {accounts?.length === 0 && (
        <Notice kind="warn">
          No {label.plural} found in Windsor. Select the client&rsquo;s account under{' '}
          {label.where} in Windsor.ai first, then reopen this.
        </Notice>
      )}

      {accounts && accounts.length > 0 && (
        <div className="table-wrap">
          <table>
            <tbody>
              {accounts.map((account) => {
                const elsewhere =
                  account.linked_client !== null && account.linked_client.slug !== slug;
                const here = account.linked_client?.slug === slug;
                return (
                  <tr key={account.account_id} style={{ opacity: elsewhere ? 0.55 : 1 }}>
                    <td style={{ width: 28 }}>
                      <input
                        type="radio"
                        name="windsor-account"
                        style={{ width: 'auto' }}
                        disabled={elsewhere || here}
                        checked={selected === account.account_id}
                        onChange={() => setSelected(account.account_id)}
                        aria-label={account.account_name ?? account.account_id}
                      />
                    </td>
                    <td>
                      <strong>{account.account_name ?? account.account_id}</strong>
                      <span className="muted mono truncate">{account.external_id}</span>
                    </td>
                    <td className="muted">
                      {here
                        ? 'Linked to this client'
                        : elsewhere
                          ? `Linked to ${account.linked_client!.name}`
                          : 'Available'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="row" style={{ marginTop: 14 }}>
        <button className="primary shrink" disabled={!selected || busy} onClick={link}>
          {busy ? 'Linking…' : `Link ${label.noun}`}
        </button>
        <button className="shrink" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
